<template>
  <draggable
    v-if="reorderable && (listKey || !managed)"
    v-model="localIds"
    class="task-ordering-list"
    tag="div"
    role="list"
    :item-key="itemKeyFn"
    :disabled="disabled"
    v-bind="dragOptions"
    @start="onDragStart"
    @end="onDragEnd"
  >
    <template #item="{ element: id, index }">
      <div class="reorder-item">
        <slot name="item" :id="id" :index="index" />
      </div>
    </template>
    <template v-if="$slots.footer" #footer>
      <slot name="footer" />
    </template>
  </draggable>
  <div v-else class="task-ordering-static">
    <div v-for="id in displayIds" :key="id" class="task-ordering-row">
      <slot name="item" :id="id" :index="displayIds.indexOf(id)" />
    </div>
    <slot name="footer" />
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  PropType,
  ref,
  toRefs,
  watch,
} from "vue";
import draggable from "vuedraggable/src/vuedraggable";

import { useOrderableTaskList } from "./ordering";

const itemKeyFn = (id: string) => id;

/** Drag-to-reorder only starts from the selected-row handle in TaskInline. */
export const TASK_REORDER_HANDLE = ".task-reorder-handle";

export default defineComponent({
  name: "Ordering",
  components: { draggable },
  props: {
    /** Ranking key; when unset with `managed`, ids are shown as passed. */
    listKey: { type: String, required: false, default: undefined },
    ids: { type: Array as PropType<string[]>, required: true },
    disabled: { type: Boolean, default: false },
    /** When false, only apply stored order (no Sortable drag). Used with external DnD. */
    reorderable: { type: Boolean, default: true },
    /**
     * When true (default), this component loads/applies/persists order for `listKey`.
     * When false, `ids` must already be ordered; `@reorder` fires on drag.
     */
    managed: { type: Boolean, default: true },
  },
  emits: {
    reorder: (_visibleAfter: string[]) => true,
  },
  setup(props, { emit }) {
    const { listKey, ids, managed } = toRefs(props);
    const listKeyRef = computed(() => listKey.value);
    const idsRef = computed(() => ids.value);

    const storeOrdering = managed.value
      ? useOrderableTaskList(listKeyRef, idsRef)
      : null;
    if (storeOrdering) {
      onBeforeUnmount(storeOrdering.release);
    }

    const displayIds = computed(() => {
      if (storeOrdering && listKey.value) {
        return storeOrdering.orderedIdsReadonly.value;
      }
      return ids.value;
    });

    const localIds = ref<string[]>([]);
    const isDragging = ref(false);

    watch(
      displayIds,
      (next) => {
        if (isDragging.value) return;
        localIds.value = [...next];
      },
      { immediate: true }
    );

    const dragOptions = computed(() => ({
      animation: 200,
      ghostClass: "task-order-ghost",
      chosenClass: "task-order-chosen",
      handle: TASK_REORDER_HANDLE,
    }));

    const onDragStart = () => {
      isDragging.value = true;
    };

    const onDragEnd = () => {
      const visibleAfter = [...localIds.value];
      if (storeOrdering && listKey.value) {
        storeOrdering.commitOrder(visibleAfter);
      }
      emit("reorder", visibleAfter);
      isDragging.value = false;
    };

    return {
      displayIds,
      dragOptions,
      itemKeyFn,
      localIds,
      onDragEnd,
      onDragStart,
    };
  },
});
</script>

<style scoped lang="scss">
.task-ordering-static {
  width: 100%;
}

.task-ordering-row {
  width: 100%;
}

.task-ordering-list {
  width: 100%;
}

:deep(.reorder-item) {
  width: 100%;
}

// Placeholder at the index the row will occupy on release.
:deep(.task-order-ghost) {
  opacity: 0.45;
  @include themed() {
    background-color: t($grey-05);
    box-shadow: inset 0 0 0 1px t($secondary-30);
  }
}

// Row being dragged (Sortable moves it among siblings).
:deep(.task-order-chosen) {
  @include themed() {
    background-color: t($grey-05);
  }
}
</style>
